/**
 * Sanitizes the filename based on Microsoft's documented invalid names and
 * characters.
 *
 * @param filename the string to be sanitized.
 * @param defaultFileName a fallback to be used when the sanitized filename is still invalid.
 * @returns a string that is a valid Microsoft Graph API file name.
 */
export function safeMicrosoftFileName(
    filename: string,
    defaultFileName: string,
): string {
    const safeCharactersName = removeInvalidFilenameCharacters(filename);
    if (safeCharactersName.length === 0) {
        return defaultFileName;
    }
    if (isProtectedName(safeCharactersName)) {
        return `${defaultFileName} ${safeCharactersName}`;
    }

    return safeCharactersName;
}

// https://support.microsoft.com/en-us/office/restrictions-and-limitations-in-onedrive-and-sharepoint-64883a5d-228e-48f5-b3d2-eb39e07630fa#invalidcharacters
const invalidCharsRegEx = RegExp(`[\\/:*?<>|]`, "g");

/** Sanitizes the filename based on Microsoft's documented invalid sequences and
 * characters.
 *
 * @param filename The string to sanitize.
 * @returns The filename with invalid characters removed.  May be an empty string.
 */
export function removeInvalidFilenameCharacters(filename: string): string {
    let resultingFilename: string;
    resultingFilename = filename.replaceAll('"', "'"); // replace double quote with single quote
    resultingFilename = resultingFilename.replaceAll(invalidCharsRegEx, "-"); // replace any invalid char with a dash
    resultingFilename = resultingFilename.replaceAll("_vti_", ""); // remove any occurence of the protected sequence
    resultingFilename = resultingFilename.trim();

    while (resultingFilename.endsWith(".")) {
        // cannot end with a period
        resultingFilename = resultingFilename
            .substring(0, resultingFilename.length - 1)
            .trimEnd();
    }
    while (resultingFilename.startsWith(".")) {
        // cannot start with a period
        resultingFilename = resultingFilename.substring(1).trimStart();
    }

    return resultingFilename;
}

// https://support.microsoft.com/en-us/office/restrictions-and-limitations-in-onedrive-and-sharepoint-64883a5d-228e-48f5-b3d2-eb39e07630fa#invalidfilefoldernames
const protectedNamesRegEx = RegExp(
    `^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9]|desktop\\.ini)(\\..*)?$`,
);
const shellPrefix = RegExp(`^~\\$.*`);
/**
 * Tests if a filename is a protected name in the context of Microsoft Graph.
 *
 * @param filename The string to test.
 * @returns `true` if the filename matches protected name formats.
 */
export function isProtectedName(filename: string): boolean {
    return protectedNamesRegEx.test(filename) || shellPrefix.test(filename);
}
